import {
    getWishlist,
    addToWishlist as addToWishlistApi,
    removeFromWishlist as removeFromWishlistApi,
    moveToCart as moveToCartApi,
} from "../api/wishlist.api";
import AuthContext from "./AuthContext";
import CartContext from "./CartContext";

// NOTE: createContext, useState, useEffect, useCallback, useMemo, useContext are auto-imported by unplugin-auto-import

const WishlistContext = createContext(null);

export const WishlistProvider = ({ children }) => {
    const { isAuthenticated } = useContext(AuthContext);
    const { refreshCart } = useContext(CartContext);

    const [wishlist, setWishlist] = useState([]);
    const [wishlistLoading, setWishlistLoading] = useState(false);
    const [wishlistCount, setWishlistCount] = useState(0);

    const fetchWishlist = useCallback(async () => {
        try {
            setWishlistLoading(true);
            const response = await getWishlist();
            const items = response.data.data || response.data || [];
            const wishlistItems = Array.isArray(items) ? items : items.items || [];
            setWishlist(wishlistItems);
            setWishlistCount(wishlistItems.length);
        } catch {
            setWishlist([]);
            setWishlistCount(0);
        } finally {
            setWishlistLoading(false);
        }
    }, []);

    const addToWishlist = useCallback(
        async (variantId) => {
            const response = await addToWishlistApi({ variantId });
            await fetchWishlist();
            return response;
        },
        [fetchWishlist]
    );

    const removeFromWishlist = useCallback(
        async (variantId) => {
            const response = await removeFromWishlistApi(variantId);
            await fetchWishlist();
            return response;
        },
        [fetchWishlist]
    );

    const moveToCart = useCallback(
        async (variantId) => {
            const response = await moveToCartApi(variantId);
            await fetchWishlist();
            if (refreshCart) {
                await refreshCart();
            }
            return response;
        },
        [fetchWishlist, refreshCart]
    );

    const isInWishlist = useCallback(
        (variantId) => {
            return wishlist.some(
                (item) =>
                    item.variantId === variantId ||
                    item.variant?._id === variantId ||
                    item._id === variantId
            );
        },
        [wishlist]
    );

    // Fetch wishlist when authentication state changes
    useEffect(() => {
        if (isAuthenticated) {
            fetchWishlist();
        } else {
            setWishlist([]);
            setWishlistCount(0);
        }
    }, [isAuthenticated, fetchWishlist]);

    const value = useMemo(
        () => ({
            wishlist,
            wishlistLoading,
            wishlistCount,
            addToWishlist,
            removeFromWishlist,
            moveToCart,
            isInWishlist,
            refreshWishlist: fetchWishlist,
        }),
        [wishlist, wishlistLoading, wishlistCount, addToWishlist, removeFromWishlist, moveToCart, isInWishlist, fetchWishlist]
    );

    return (
        <WishlistContext.Provider value={value}>
            {children}
        </WishlistContext.Provider>
    );
};

export const useWishlistContext = () => {
    const context = useContext(WishlistContext);
    if (!context) {
        throw new Error('useWishlistContext must be used within a WishlistProvider');
    }
    return context;
};

export { WishlistContext };
export default WishlistContext;
